// React Query hooks for the skip stock types + sizes settings surface.
//
// Server endpoints live under /api/v1/skip/stock — see
// server/src/modules/skip/stock/router.ts for the full contract.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../../../../api/client';
import { endpoints } from '../../../../api/endpoints';
import { queryKeys } from '../../../../api/queryKeys';
import type {
  CreateSkipStockSizeInput,
  CreateSkipStockTypeInput,
  SkipStockSize,
  SkipStockType,
  SkipStockTypeWithSizes,
  UpdateSkipStockSizeInput,
  UpdateSkipStockTypeInput,
} from '../../../../types/api';

// ----------------------------------------------------------------- TYPES

/**
 * List stock types for the calling tenant.
 *
 * Default mode (no options) returns every global alongside own-tenant
 * types — the SkipStockTypesPage uses this so it can still render a
 * toggle for non-activated globals.
 *
 * Pass `{ forSelection: true }` for inventory / job / enquiry selectors
 * so non-activated globals are filtered server-side. Cache key carries
 * the flag so the two modes don't share entries.
 */
export function useSkipStockTypes(options: { forSelection?: boolean } = {}) {
  const forSelection = options.forSelection ?? false;
  return useQuery<{ stockTypes: SkipStockType[] }>({
    queryKey: queryKeys.skipStockTypes.list({ forSelection }),
    queryFn: async () => {
      const res = await apiClient.get<{ stockTypes: SkipStockType[] }>(
        endpoints.skipStock.types,
        forSelection ? { params: { for_selection: 'true' } } : undefined,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useSkipStockType(encId: string | null | undefined) {
  return useQuery<{ stockType: SkipStockTypeWithSizes }>({
    queryKey: queryKeys.skipStockTypes.detail(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useSkipStockType called without enc_id');
      const res = await apiClient.get<{ stockType: SkipStockTypeWithSizes }>(
        endpoints.skipStock.type(encId),
      );
      return res.data;
    },
    enabled: !!encId,
    staleTime: 30_000,
  });
}

export function useCreateSkipStockType() {
  const qc = useQueryClient();
  return useMutation<{ stockType: SkipStockTypeWithSizes }, Error, CreateSkipStockTypeInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<
        { stockType: SkipStockTypeWithSizes },
        CreateSkipStockTypeInput
      >(endpoints.skipStock.types, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipSizes.list });
    },
  });
}

export function useUpdateSkipStockType(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ stockType: SkipStockTypeWithSizes }, Error, UpdateSkipStockTypeInput>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<
        { stockType: SkipStockTypeWithSizes },
        UpdateSkipStockTypeInput
      >(endpoints.skipStock.type(encId), body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.detail(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.skipSizes.list });
    },
  });
}

export function useDeleteSkipStockType(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: boolean }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: boolean }>(endpoints.skipStock.type(encId));
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipSizes.list });
    },
  });
}

// ---------------------------------------------------- GLOBAL ACTIVATION

/**
 * Activate or deactivate a global default stock type for the calling
 * tenant. Both calls are idempotent server-side.
 *
 * Caller is responsible for the optimistic update + revert-on-error
 * pattern; this hook only owns the network round-trip and cache
 * invalidation. The settings page does its own optimistic update so the
 * toggle reflects immediately, then invalidates here on settle.
 */
export function useActivateGlobalStockType() {
  const qc = useQueryClient();
  return useMutation<{ stockType: SkipStockType }, Error, { encId: string }>({
    mutationFn: async ({ encId }) => {
      const res = await apiClient.post<{ stockType: SkipStockType }, undefined>(
        endpoints.skipStock.activateGlobal(encId),
        undefined,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipSizes.list });
    },
  });
}

export function useDeactivateGlobalStockType() {
  const qc = useQueryClient();
  return useMutation<{ stockType: SkipStockType }, Error, { encId: string }>({
    mutationFn: async ({ encId }) => {
      const res = await apiClient.delete<{ stockType: SkipStockType }>(
        endpoints.skipStock.activateGlobal(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipSizes.list });
    },
  });
}

// ----------------------------------------------------------------- SIZES

export function useSkipStockSizes(typeEncId: string | null | undefined) {
  return useQuery<{ sizes: SkipStockSize[] }>({
    queryKey: queryKeys.skipStockTypes.sizes(typeEncId ?? ''),
    queryFn: async () => {
      if (!typeEncId) throw new Error('useSkipStockSizes called without typeEncId');
      const res = await apiClient.get<{ sizes: SkipStockSize[] }>(
        endpoints.skipStock.sizes(typeEncId),
      );
      return res.data;
    },
    enabled: !!typeEncId,
    staleTime: 30_000,
  });
}

export function useCreateSkipStockSize(typeEncId: string) {
  const qc = useQueryClient();
  return useMutation<{ size: SkipStockSize }, Error, CreateSkipStockSizeInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ size: SkipStockSize }, CreateSkipStockSizeInput>(
        endpoints.skipStock.sizes(typeEncId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.sizes(typeEncId) });
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipSizes.list });
    },
  });
}

export function useUpdateSkipStockSize(typeEncId: string, sizeEncId: string) {
  const qc = useQueryClient();
  return useMutation<{ size: SkipStockSize }, Error, UpdateSkipStockSizeInput>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<{ size: SkipStockSize }, UpdateSkipStockSizeInput>(
        endpoints.skipStock.size(typeEncId, sizeEncId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.sizes(typeEncId) });
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipSizes.list });
    },
  });
}

export function useDeleteSkipStockSize(typeEncId: string, sizeEncId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: boolean }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: boolean }>(
        endpoints.skipStock.size(typeEncId, sizeEncId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.sizes(typeEncId) });
      void qc.invalidateQueries({ queryKey: queryKeys.skipStockTypes.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipSizes.list });
    },
  });
}
